
import type {
  PlacementItem,
  PlacementProposalView,
  PlacementSkipped,
  PlacementUnmatched,
} from './types';

export interface TextPart {
  text: string;
  anchor: boolean;
}

export type FeedRow =
  | { kind: 'text'; key: string; segmentIndex: number; parts: TextPart[] }
  | { kind: 'insert'; key: string; item: PlacementItem; anchored: boolean }
  | { kind: 'gap'; key: string; gap: PlacementUnmatched; anchored: boolean }
  | { kind: 'skip'; key: string; skip: PlacementSkipped; anchored: boolean };

interface Mark {
  startWord: number;
  anchorText: string;
  row: (anchored: boolean) => FeedRow;
}

function marksOfSegment(proposal: PlacementProposalView, segmentIndex: number): Mark[] {
  const marks: Mark[] = [];

  for (const item of proposal.items) {
    if (item.segmentIndex !== segmentIndex) continue;
    marks.push({
      startWord: item.startWord,
      anchorText: item.anchorText,
      row: (anchored) => ({ kind: 'insert', key: `item-${item.order}`, item, anchored }),
    });
  }
  for (const gap of proposal.unmatched) {
    if (gap.segmentIndex !== segmentIndex) continue;
    marks.push({
      startWord: gap.startWord,
      anchorText: gap.anchorText,
      row: (anchored) => ({
        kind: 'gap',
        key: `gap-${gap.segmentIndex}-${gap.startWord}`,
        gap,
        anchored,
      }),
    });
  }
  for (const skip of proposal.skipped) {
    if (skip.segmentIndex !== segmentIndex) continue;
    marks.push({
      startWord: skip.startWord,
      anchorText: skip.anchorText,
      row: (anchored) => ({
        kind: 'skip',
        key: `skip-${skip.segmentIndex}-${skip.startWord}-${skip.reason}`,
        skip,
        anchored,
      }),
    });
  }

  return marks.sort((a, b) => a.startWord - b.startWord);
}

function textRow(segmentIndex: number, parts: TextPart[], seq: number): FeedRow | null {
  const meaningful = parts.filter((part) => part.text.length > 0);
  if (meaningful.length === 0) return null;
  return { kind: 'text', key: `text-${segmentIndex}-${seq}`, segmentIndex, parts: meaningful };
}

export function buildFeed(proposal: PlacementProposalView): FeedRow[] {
  const rows: FeedRow[] = [];

  proposal.segments.forEach((segment, segmentIndex) => {
    const marks = marksOfSegment(proposal, segmentIndex);
    const haystack = segment.toLowerCase();
    let cursor = 0;
    let seq = 0;
    const orphans: Mark[] = [];

    for (const mark of marks) {
      const needle = mark.anchorText.trim().toLowerCase();
      const at = needle ? haystack.indexOf(needle, cursor) : -1;
      if (at < 0) {
        orphans.push(mark);
        continue;
      }
      const before = segment.slice(cursor, at);
      const anchor = segment.slice(at, at + needle.length);
      const row = textRow(
        segmentIndex,
        [
          { text: before, anchor: false },
          { text: anchor, anchor: true },
        ],
        seq,
      );
      if (row) rows.push(row);
      seq += 1;
      cursor = at + needle.length;
      rows.push(mark.row(true));
    }

    const tail = textRow(segmentIndex, [{ text: segment.slice(cursor), anchor: false }], seq);
    if (tail) rows.push(tail);
    for (const mark of orphans) rows.push(mark.row(false));
  });

  return rows;
}

export function orphanRows(proposal: PlacementProposalView, feed: FeedRow[]): FeedRow[] {
  const shown = new Set(feed.map((row) => row.key));
  const rows: FeedRow[] = [];
  for (const item of proposal.items) {
    const key = `item-${item.order}`;
    if (!shown.has(key)) rows.push({ kind: 'insert', key, item, anchored: false });
  }
  for (const gap of proposal.unmatched) {
    const key = `gap-${gap.segmentIndex}-${gap.startWord}`;
    if (!shown.has(key)) rows.push({ kind: 'gap', key, gap, anchored: false });
  }
  return rows;
}
